import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { submitCareer } from "@/lib/api";
import { API } from "@/lib/api";
import axios from "axios";

const schema = z.object({
  application_type: z.enum(["join", "coop"], {
    errorMap: () => ({ message: "اختر نوع الطلب" }),
  }),
  full_name: z.string().min(2, "الرجاء إدخال الاسم الكامل"),
  email: z.string().email("بريد إلكتروني غير صحيح"),
  mobile: z.string().min(6, "رقم جوال غير صحيح"),
  organization: z.string().optional().or(z.literal("")),
  field_or_major: z.string().optional().or(z.literal("")),
  profile_url: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine(
      (v) => !v || /^https?:\/\/.+/i.test(v),
      "يجب أن يبدأ الرابط بـ http أو https",
    ),
  message: z.string().min(10, "اكتب رسالة قصيرة على الأقل (١٠ أحرف)"),
  cv_url: z.string().optional().or(z.literal("")),
  cv_filename: z.string().optional().or(z.literal("")),
  privacy_consent: z.literal(true, {
    errorMap: () => ({ message: "يجب الموافقة على سياسة الخصوصية" }),
  }),
}).refine(
  (data) => !!data.cv_url,
  { message: "يُشترط رفع السيرة الذاتية بصيغة PDF", path: ["cv_url"] },
);

const fieldBase =
  "w-full bg-white border border-navy/20 rounded-none px-4 py-3 text-navy focus-visible:ring-2 focus-visible:ring-offset-0 outline-none";

export default function CareerForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [serverError, setServerError] = useState("");

  const preType = params.get("type") === "coop" ? "coop" : "join";

  const hidden = useMemo(
    () => ({
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      referrer: typeof document !== "undefined" ? document.referrer : "",
      landing_path:
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : "",
    }),
    [params],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      application_type: preType,
      full_name: "",
      email: "",
      mobile: "",
      organization: "",
      field_or_major: "",
      profile_url: "",
      message: "",
      cv_url: "",
      cv_filename: "",
      privacy_consent: false,
    },
  });

  useEffect(() => {
    setValue("application_type", preType);
  }, [preType, setValue]);

  const appType = watch("application_type");
  const isCoop = appType === "coop";
  const cvUrl = watch("cv_url");
  const cvFilename = watch("cv_filename");
  const [cvUploading, setCvUploading] = useState(false);
  const [cvError, setCvError] = useState("");

  const onCvChange = async (e) => {
    setCvError("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      setCvError("يُقبل PDF فقط.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setCvError("حجم الملف كبير (الحد الأقصى 5MB).");
      return;
    }
    setCvUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/careers/upload-cv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setValue("cv_url", data.cv_url, { shouldValidate: true });
      setValue("cv_filename", data.cv_filename || f.name, { shouldValidate: true });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setCvError(typeof msg === "string" ? msg : "فشل رفع الملف. حاول مجددًا.");
      setValue("cv_url", "", { shouldValidate: true });
      setValue("cv_filename", "", { shouldValidate: true });
    } finally {
      setCvUploading(false);
    }
  };

  const clearCv = () => {
    setValue("cv_url", "", { shouldValidate: true });
    setValue("cv_filename", "", { shouldValidate: true });
    setCvError("");
  };

  const onSubmit = async (data) => {
    setServerError("");
    try {
      const res = await submitCareer({
        ...data,
        ...hidden,
        company_website: "",
      });
      if (res?.status === "ok") {
        navigate("/thank-you");
      } else {
        setServerError("تعذّر إرسال الطلب. حاول مجددًا.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setServerError(
        typeof msg === "string"
          ? msg
          : "حدث خطأ أثناء الإرسال. تأكد من البيانات وحاول مجددًا.",
      );
    }
  };

  const Err = ({ name }) =>
    errors[name] ? (
      <p
        className="mt-1.5 text-sm flex items-center gap-1.5"
        style={{ color: "var(--coral)" }}
        role="alert"
      >
        <AlertCircle size={14} />
        {errors[name].message}
      </p>
    ) : null;

  const TypeButton = ({ value, title, sub }) => {
    const active = appType === value;
    return (
      <button
        type="button"
        data-testid={`career-type-${value}`}
        onClick={() => setValue("application_type", value, { shouldValidate: true })}
        aria-pressed={active}
        className={`flex-1 text-right px-5 py-4 border transition-colors ${
          active
            ? "border-[var(--coral)] bg-white"
            : "border-navy/15 bg-white/60 hover:bg-white"
        }`}
      >
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 rotate-45"
            style={{
              background: active ? "var(--coral)" : "transparent",
              border: `1px solid ${active ? "var(--coral)" : "rgba(40,62,85,0.3)"}`,
            }}
          />
          <span className="font-display text-navy text-lg">{title}</span>
        </div>
        <div className="mt-1.5 text-sm text-blue/80">{sub}</div>
      </button>
    );
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      data-testid="career-form"
      className="space-y-6"
    >
      {/* Honeypot */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          opacity: 0,
          height: 0,
          overflow: "hidden",
        }}
      >
        <label htmlFor="company_website_c">الموقع الإلكتروني</label>
        <input
          id="company_website_c"
          type="text"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {/* Application type selector */}
      <div>
        <div className="mb-2 font-medium text-navy">نوع الطلب <span style={{ color: "var(--coral)" }}>*</span></div>
        <div className="flex flex-col sm:flex-row gap-3">
          <TypeButton
            value="join"
            title="انضمام إلى فريق سياق"
            sub="فرص التوظيف بدوام كامل أو جزئي."
          />
          <TypeButton
            value="coop"
            title="التدريب التعاوني"
            sub="طلاب وطالبات الجامعات — فصل تدريبي."
          />
        </div>
        <input type="hidden" {...register("application_type")} />
        <Err name="application_type" />
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="full_name_c" className="block mb-2 font-medium text-navy">
            الاسم الكامل <span style={{ color: "var(--coral)" }}>*</span>
          </label>
          <input
            id="full_name_c"
            autoComplete="name"
            className={fieldBase}
            data-testid="career-full-name"
            {...register("full_name")}
          />
          <Err name="full_name" />
        </div>
        <div>
          <label htmlFor="email_c" className="block mb-2 font-medium text-navy">
            البريد الإلكتروني <span style={{ color: "var(--coral)" }}>*</span>
          </label>
          <input
            id="email_c"
            type="email"
            autoComplete="email"
            dir="ltr"
            className={`${fieldBase} text-right`}
            data-testid="career-email"
            {...register("email")}
          />
          <Err name="email" />
        </div>
        <div>
          <label htmlFor="mobile_c" className="block mb-2 font-medium text-navy">
            الجوال <span style={{ color: "var(--coral)" }}>*</span>
          </label>
          <input
            id="mobile_c"
            type="tel"
            autoComplete="tel"
            dir="ltr"
            className={`${fieldBase} text-right`}
            data-testid="career-mobile"
            {...register("mobile")}
          />
          <Err name="mobile" />
        </div>
        <div>
          <label htmlFor="organization_c" className="block mb-2 font-medium text-navy">
            {isCoop ? "الجامعة" : "جهة العمل الحالية"}
          </label>
          <input
            id="organization_c"
            autoComplete="organization"
            className={fieldBase}
            data-testid="career-organization"
            {...register("organization")}
          />
        </div>
        <div>
          <label htmlFor="field_or_major_c" className="block mb-2 font-medium text-navy">
            {isCoop ? "التخصص" : "المجال المهني"}
          </label>
          <input
            id="field_or_major_c"
            className={fieldBase}
            data-testid="career-field"
            {...register("field_or_major")}
          />
        </div>
        <div>
          <label htmlFor="profile_url_c" className="block mb-2 font-medium text-navy">
            رابط الملف / LinkedIn (اختياري)
          </label>
          <input
            id="profile_url_c"
            type="url"
            dir="ltr"
            placeholder="https://…"
            className={`${fieldBase} text-right`}
            data-testid="career-profile-url"
            {...register("profile_url")}
          />
          <Err name="profile_url" />
        </div>
      </div>

      <div data-testid="career-cv-wrap">
          <label htmlFor="career_cv" className="block mb-2 font-medium text-navy">
            السيرة الذاتية (PDF) <span style={{ color: "var(--coral)" }}>*</span>
          </label>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <input
              id="career_cv"
              type="file"
              accept="application/pdf,.pdf"
              onChange={onCvChange}
              data-testid="career-cv-input"
              className="block text-sm text-navy file:me-4 file:py-3 file:px-5 file:border file:border-navy/20 file:bg-white file:text-navy file:font-medium hover:file:bg-paper cursor-pointer"
            />
            {cvUploading && (
              <span className="inline-flex items-center gap-2 text-sm text-blue">
                <Loader2 size={14} className="animate-spin" /> جارٍ الرفع…
              </span>
            )}
            {!cvUploading && cvUrl && (
              <div className="inline-flex items-center gap-3 text-sm">
                <span className="text-navy font-medium truncate max-w-[280px]" title={cvFilename}>
                  {cvFilename || "تم الرفع"}
                </span>
                <button
                  type="button"
                  onClick={clearCv}
                  className="text-coral hover:underline"
                  data-testid="career-cv-clear"
                >
                  إزالة
                </button>
              </div>
            )}
          </div>
          <p className="mt-1.5 text-xs text-blue/70">
            PDF فقط، الحد الأقصى 5MB.
          </p>
          {cvError && (
            <p
              className="mt-1.5 text-sm flex items-center gap-1.5"
              style={{ color: "var(--coral)" }}
              role="alert"
            >
              <AlertCircle size={14} />
              {cvError}
            </p>
          )}
          <Err name="cv_url" />
          <input type="hidden" {...register("cv_url")} />
          <input type="hidden" {...register("cv_filename")} />
        </div>

      <div>
        <label htmlFor="message_c" className="block mb-2 font-medium text-navy">
          {isCoop
            ? "لماذا تختار سياقًا لتدريبك التعاوني؟"
            : "لماذا ترغب بالانضمام إلى فريق سياق؟"}{" "}
          <span style={{ color: "var(--coral)" }}>*</span>
        </label>
        <textarea
          id="message_c"
          rows={5}
          className={fieldBase}
          data-testid="career-message"
          placeholder="اكتب باختصار خبراتك أو اهتماماتك ودوافعك للانضمام…"
          {...register("message")}
        />
        <Err name="message" />
      </div>

      <div className="space-y-4 pt-2">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1.5 h-4 w-4 accent-[var(--coral)]"
            data-testid="career-consent"
            {...register("privacy_consent")}
          />
          <span className="text-navy leading-relaxed">
            أوافق على{" "}
            <a href="/privacy" className="link-underline" style={{ color: "var(--coral)" }}>
              سياسة الخصوصية
            </a>{" "}
            ومعالجة بياناتي لغرض التواصل معي بشأن الطلب.{" "}
            <span style={{ color: "var(--coral)" }}>*</span>
          </span>
        </label>
        <Err name="privacy_consent" />
      </div>

      {serverError && (
        <div
          className="border p-4 flex items-center gap-2"
          style={{ borderColor: "var(--coral)", color: "var(--coral)" }}
          role="alert"
          data-testid="career-server-error"
        >
          <AlertCircle size={18} />
          {serverError}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        data-testid="career-submit"
        className="group inline-flex items-center gap-2 bg-coral text-white px-8 py-4 text-base font-semibold hover:bg-[#d85a5e] transition-colors disabled:opacity-60"
        style={{ backgroundColor: "var(--coral)" }}
      >
        {isSubmitting ? (
          <>
            <Loader2 size={18} className="animate-spin" /> جارٍ الإرسال…
          </>
        ) : (
          <>
            أرسل الطلب{" "}
            <ArrowLeft
              size={18}
              className="transition-transform group-hover:-translate-x-1"
            />
          </>
        )}
      </button>
    </form>
  );
}
